import {useMemo} from 'react';
import {kr} from '../../lib/format.js';
import {Defs, fillById, niceScale, Grid, Legend} from './primitives.jsx';

const W = 560, H = 300, L = 60, R = 10, T = 10, B = 42;

/**
 * The CURRENT position, split by how late it is.
 *
 * This is deliberately not a time series: the dashboard already draws outstanding and
 * overdue month by month, and that chart cannot answer the question this one exists for
 * — "of the money owed to us right now, how old is it". A single 409 kkr receivable
 * figure hides the difference between one invoice due next week and eight invoices from
 * last winter, and that difference is the whole of a collection conversation.
 *
 * Receivables and payables share the axis so the two are directly comparable. They are
 * not netted: a payable falling due in September is not cancelled by a receivable that
 * may arrive in December, and presenting a net figure invites exactly that assumption.
 */
export default function AgingProfile({receivable, payable}) {
	const buckets = receivable.profile.map(b => b.label);
	const g = useMemo(() => {
		const hi = Math.max(0, ...receivable.profile.map(b => b.amount), ...payable.profile.map(b => b.amount));
		const sc = niceScale(0, hi);
		const iw = W - L - R, ih = H - T - B;
		return {
			sc,
			y: v => T + ih - ((v - sc.lo) / (sc.hi - sc.lo)) * ih,
			x: i => L + (iw * (i + 0.5)) / buckets.length,
			bw: Math.max(6, Math.min(30, (iw / buckets.length) * 0.34)),
		};
	}, [receivable, payable, buckets.length]);

	const zero = g.y(0);
	const bars = (rows, offset, paint) => rows.map((b, i) => {
		if (!b.amount) return null;
		const yv = g.y(b.amount);
		return (
			<g key={`${offset}-${b.key}`}>
				<rect x={g.x(i) + offset - g.bw / 2} y={yv} width={g.bw}
					height={Math.max(zero - yv, 0.6)} rx={2} {...paint} />
				<text x={g.x(i) + offset} y={yv - 5} className="axs" textAnchor="middle">
					{b.amount ? kr(b.amount) : ''}
				</text>
			</g>
		);
	});

	return (
		<div className="chart">
			<div className="chart__head">
				<span className="chart__title">Förfalloprofil idag</span>
				<span className="chart__hint">
					öppna poster efter hur länge de varit förfallna. Fordringar och skulder
					kvittas inte mot varandra — de förfaller inte samtidigt.
				</span>
			</div>
			<div className="chart__canvas">
				<svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" role="img"
					aria-label="Öppna kundfordringar och leverantörsskulder efter förfalloålder">
					<Defs />
					<Grid {...g.sc} y={g.y} L={L} R={R} W={W} />
					{bars(receivable.profile, -g.bw * 0.55, fillById('solid'))}
					{bars(payable.profile, g.bw * 0.55, fillById('hatch'))}
					<line x1={L} x2={W - R} y1={zero} y2={zero} stroke="#000" strokeWidth={1} />
					{buckets.map((b, i) => (
						<g key={b}>
							<text x={g.x(i)} y={H - 24} className="axs" textAnchor="middle">{b}</text>
							<text x={g.x(i)} y={H - 10} className="ax" textAnchor="middle">
								{receivable.profile[i].count} / {payable.profile[i].count} st
							</text>
						</g>
					))}
				</svg>
			</div>
			<Legend items={[
				{label: `Kundfordringar · ${kr(receivable.total)} kr`, fill: 'solid'},
				{label: `Leverantörsskulder · ${kr(payable.total)} kr`, fill: 'hatch'},
			]} />
		</div>
	);
}
